import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import CTASection from "../components/home/CTASection";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import useReveal from "../hooks/useReveal";
import { useEstimate } from "../components/EstimateContext";
import {
  TrendingUp, Package, Sun, Leaf, Sparkles, Heart, Star, MessageSquare, PenTool, Layers,
  Hammer, CheckCircle2, Plus, Minus, ArrowRight, Check, LayoutGrid, Lightbulb, Droplet,
  Moon, Smartphone, ShowerHead, Bath, Wrench,
} from "lucide-react";
import {
  IMG, BENEFITS, PROCESS, STYLES, LAYOUTS, VANITY_STYLES, VANITY_MATERIALS, COUNTERTOPS,
  STORAGE, SHOWER_FEATURES, BATHTUBS, TILE_FLOOR, TILE_WALL, TILE_SHOWER, FLOORING, LIGHTING,
  HARDWARE, MIRRORS, SMART_TECH, HIDDEN_LUXURY, SPA, WELLNESS, INVISIBLE, ARCHITECTURAL,
  HOTEL, SMALL_BATH, ACCESSIBILITY, VENTILATION, GALLERY, FAQ,
} from "../data/bathroomData";

const ICONS = { TrendingUp, Package, Sun, Leaf, Sparkles, Heart, Star, MessageSquare, PenTool, Layers, Hammer, CheckCircle2, Lightbulb, Droplet, Moon, Smartphone };

const Heading = ({ eyebrow, title, sub, center }) => (
  <div className={`${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"} mb-12 reveal`}>
    <div className={`flex items-center gap-3 mb-4 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-10 bg-[#2f9fe6]" />
      <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">{eyebrow}</span>
      {center && <span className="h-px w-10 bg-[#2f9fe6]" />}
    </div>
    <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight">{title}</h2>
    {sub && <p className="text-[#6b7683] text-lg mt-4">{sub}</p>}
  </div>
);

const FeatureList = ({ items, dark, testid }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={testid}>
    {items.map((f, i) => (
      <div
        key={f}
        className={`reveal delay-${(i % 3) + 1} flex items-center gap-3 rounded-xl px-5 py-4 border transition-all ${
          dark ? "bg-white/5 border-white/10 hover:border-[#2f9fe6]" : "bg-white border-[#eef1f4] hover:shadow-md"
        }`}
      >
        <span className="w-7 h-7 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-white" /></span>
        <span className={`font-medium ${dark ? "text-[#dbe7f1]" : "text-[#0f2740]"}`}>{f}</span>
      </div>
    ))}
  </div>
);

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-3">
    {items.map((d) => (
      <span key={d} className="px-6 py-3 rounded-full bg-[#f2f5f8] text-[#0f2740] font-semibold hover:bg-[#2f9fe6] hover:text-white transition-colors cursor-default">{d}</span>
    ))}
  </div>
);

export default function BathroomRenovations() {
  useReveal();
  const { openEstimate } = useEstimate();
  const [openMat, setOpenMat] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <Seo title="Bathroom Renovations Edmonton" path="/services/bathroom"
        description="The complete guide to bathroom renovations in Edmonton — styles, vanities, showers, bathtubs, tile, lighting, smart features and spa design. Get a free estimate from Classic Interior Renovations."
        image={IMG.hero} />

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Luxury modern bathroom renovation in Edmonton" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
        </div>
        <div className="container-x relative z-10 pt-28 pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#bfe0f5] tracking-[0.26em] text-xs font-medium uppercase">Bathroom Renovations</span>
          </div>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 max-w-3xl">
            <span className="font-light">Your Personal</span> <span className="font-bold text-[#2f9fe6]">Spa</span>, Designed for Comfort
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-9">
            From vanities and showers to tile, lighting and smart features — the complete guide to renovating your Edmonton bathroom, and the team to build it.
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-estimate-btn" onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
              Get a Free Estimate <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link to="/projects" className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">View Our Work</Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Why Renovate" title="Designed for comfort. Built to last."
            sub="A modern bathroom should be more than just functional — it should improve your daily routine, increase your home's value, and create a relaxing space you'll enjoy for years." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon];
              return (
                <div key={b.title} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-8 border border-[#eef1f4] hover:shadow-xl hover:-translate-y-1 transition-all`}>
                  <div className="w-14 h-14 rounded-xl bg-[#eaf5fd] flex items-center justify-center mb-5"><Icon className="w-7 h-7 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{b.title}</h3>
                  <p className="text-[#6b7683] text-sm leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-[#0f2740] text-white overflow-hidden">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Our Process</span><span className="h-px w-10 bg-[#2f9fe6]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Our bathroom renovation process</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <div key={p.title} className={`reveal delay-${(i % 5) + 1} relative`}>
                  <div className="w-14 h-14 rounded-full bg-[#2f9fe6]/15 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                  <div className="text-[#2f9fe6] font-display font-bold text-sm mb-1">STEP {i + 1}</div>
                  <h3 className="font-display text-lg font-semibold mb-2">{p.title}</h3>
                  <p className="text-[#a9c6dd] text-sm">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STYLES */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Design Styles" title="Choose your style" sub="Choose the style that matches your home. Hover to explore each look." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STYLES.map((s, i) => (
              <div key={s.name} className={`reveal delay-${(i % 4) + 1} relative group shape-soft h-72 cursor-pointer`}>
                <img src={s.img} alt={`${s.name} bathroom`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/90 via-[#0f2740]/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-display text-xl font-bold text-white mb-2">{s.name}</h3>
                  <p className="text-white/80 text-sm max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LAYOUTS */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Layout Ideas" title="Bathroom layout ideas" sub="Explore layouts for every type of home." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LAYOUTS.map((l, i) => (
              <div key={l.name} className={`reveal delay-${(i % 3) + 1} bg-white rounded-2xl p-7 border border-[#eef1f4] hover:shadow-lg hover:-translate-y-1 transition-all`}>
                <div className="w-12 h-12 rounded-lg bg-[#eaf5fd] flex items-center justify-center mb-4"><LayoutGrid className="w-6 h-6 text-[#2f9fe6]" /></div>
                <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{l.name}</h3>
                <p className="text-[#6b7683] text-sm">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VANITIES & STORAGE */}
      <section className="py-24 bg-white">
        <div className="container-x space-y-16">
          <Heading center eyebrow="Vanities & Storage" title="Find the perfect vanity" sub="Beautiful bathrooms stay organized. Explore vanity styles, materials and countertops." />
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="reveal-left">
              <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-5">Vanity styles</h3>
              <Chips items={VANITY_STYLES} />
              <h3 className="font-display text-2xl font-bold text-[#0f2740] mt-10 mb-5">Vanity materials</h3>
              <div className="divide-y divide-[#e7e9ec] border-t border-[#e7e9ec]">
                {VANITY_MATERIALS.map((m, i) => (
                  <div key={m.name}>
                    <button onClick={() => setOpenMat(openMat === i ? -1 : i)} className="w-full flex items-center justify-between py-4 text-left group">
                      <span className={`font-display text-lg font-semibold ${openMat === i ? "text-[#2f9fe6]" : "text-[#0f2740] group-hover:text-[#2f9fe6]"}`}>{m.name}</span>
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center ${openMat === i ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740]"}`}>{openMat === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-400 ${openMat === i ? "max-h-40 pb-5" : "max-h-0"}`}><p className="text-[#5a6570] leading-relaxed">{m.text}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal-right">
              <div className="shape-arch-top"><img src={IMG.vanA} alt="Bathroom vanity" className="w-full h-[520px] object-cover" /></div>
            </div>
          </div>
          <div>
            <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-6 text-center">Countertop materials</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {COUNTERTOPS.map((c, i) => (
                <div key={c.name} className={`reveal delay-${(i % 4) + 1} bg-[#f7f9fb] rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-xl transition-all`}>
                  <img src={c.img} alt={`${c.name} countertop`} loading="lazy" className="w-full h-40 object-cover" />
                  <div className="p-6">
                    <h4 className="font-display text-xl font-semibold text-[#0f2740] mb-4">{c.name}</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Pros:</b> {c.pros}</span></li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Maintenance:</b> {c.maintenance}</span></li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Durability:</b> {c.durability}</span></li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Best for:</b> {c.bestFor}</span></li>
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SMART STORAGE */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x">
          <Heading center eyebrow="Storage" title="Smart storage solutions" sub="Discover innovative storage ideas designed to maximize every inch of space." />
          <FeatureList items={STORAGE} testid="storage-list" />
        </div>
      </section>

      {/* LUXURY SHOWER */}
      <section className="py-24 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left order-2 lg:order-1">
            <Heading eyebrow="Luxury Showers" title="Build your personal spa" sub="Explore premium shower features that elevate your daily routine." />
            <FeatureList items={SHOWER_FEATURES} testid="shower-list" />
          </div>
          <div className="reveal-right order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="shape-soft"><img src={IMG.showerA} alt="Walk-in shower" className="w-full h-full object-cover" /></div>
            <div className="shape-soft mt-8"><img src={IMG.showerB} alt="Frameless glass shower" className="w-full h-full object-cover" /></div>
          </div>
        </div>
      </section>

      {/* BATHTUBS */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Bathtubs" title="Bathtub collection" sub="Choose the perfect bathtub for your lifestyle." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BATHTUBS.map((b, i) => (
              <div key={b.name} className={`reveal delay-${(i % 3) + 1} group bg-white rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-xl transition-all`}>
                <div className="overflow-hidden"><img src={b.img} alt={b.name} loading="lazy" className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-1 flex items-center gap-2"><Bath className="w-5 h-5 text-[#2f9fe6]" />{b.name}</h3>
                  <p className="text-[#6b7683] text-sm">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TILE & SURFACE GUIDE */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Materials" title="Tile & surface guide" sub="Explore premium materials for floors, walls and showers." />
          <div className="grid lg:grid-cols-3 gap-8">
            {[{ t: "Floor Tiles", items: TILE_FLOOR, img: IMG.tileD }, { t: "Wall Finishes", items: TILE_WALL, img: IMG.tileC }, { t: "Shower Wall Options", items: TILE_SHOWER, img: IMG.tileA }].map((col, i) => (
              <div key={col.t} className={`reveal delay-${i + 1} bg-[#f7f9fb] rounded-2xl overflow-hidden border border-[#eef1f4]`}>
                <img src={col.img} alt={col.t} loading="lazy" className="w-full h-40 object-cover" />
                <div className="p-6">
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-4">{col.t}</h3>
                  <div className="flex flex-wrap gap-2">
                    {col.items.map((it) => (<span key={it} className="px-3.5 py-1.5 rounded-full bg-white border border-[#e2e6ea] text-[#0f2740] text-sm font-medium">{it}</span>))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLOORING */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x">
          <Heading center eyebrow="Flooring" title="Flooring options" sub="Find the perfect foundation for your bathroom." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FLOORING.map((f, i) => (
              <div key={f.name} className={`reveal delay-${(i % 3) + 1} bg-white rounded-xl p-6 border border-[#e5ddce]`}>
                <div className="font-display font-semibold text-[#0f2740] mb-1">{f.name}</div>
                <p className="text-[#6b7683] text-sm">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIGHTING */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Lighting Design" title="Layered lighting design" sub="Lighting transforms the atmosphere of your bathroom." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LIGHTING.map((l, i) => {
              const Icon = ICONS[l.icon];
              return (
                <div key={l.name} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4] hover:shadow-lg transition-all`}>
                  <div className="w-12 h-12 rounded-xl bg-[#eaf5fd] flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{l.name}</h3>
                  <p className="text-[#6b7683] text-sm">{l.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FIXTURES & MIRRORS */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <div className="reveal-left">
            <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-2 flex items-center gap-2"><Wrench className="w-6 h-6 text-[#2f9fe6]" />Fixtures & hardware</h3>
            <p className="text-[#6b7683] mb-6">Choose finishes that complement your style.</p>
            <Chips items={HARDWARE} />
          </div>
          <div className="reveal-right">
            <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-2">Mirrors & reflection</h3>
            <p className="text-[#6b7683] mb-6">The finishing touch that ties the room together.</p>
            <Chips items={MIRRORS} />
          </div>
        </div>
      </section>

      {/* SMART TECHNOLOGY */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Smart Bathroom</span><span className="h-px w-10 bg-[#2f9fe6]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">The future of bathroom design</h2>
            <p className="text-[#a9c6dd] text-lg">Modern bathrooms can be equipped with intelligent technology that adds comfort and convenience.</p>
          </div>
          <FeatureList items={SMART_TECH} dark testid="smart-tech-list" />
        </div>
      </section>

      {/* HIDDEN LUXURY */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Hidden Luxury" title="Small details. Big difference." sub="Explore luxury upgrades many homeowners never knew existed." />
          <FeatureList items={HIDDEN_LUXURY} testid="hidden-luxury-list" />
        </div>
      </section>

      {/* SPA EXPERIENCE */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="shape-arch-top"><img src={IMG.tubC} alt="Spa bathroom" className="w-full h-[520px] object-cover" /></div>
          </div>
          <div className="reveal-right">
            <Heading eyebrow="Spa at Home" title="Transform your bathroom into a wellness retreat" />
            <FeatureList items={SPA} testid="spa-list" />
          </div>
        </div>
      </section>

      {/* WELLNESS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Wellness" title="Designed for better living" sub="Discover features that improve comfort, relaxation, and overall well-being." />
          <FeatureList items={WELLNESS} testid="wellness-list" />
        </div>
      </section>

      {/* INVISIBLE DESIGN */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Invisible Design" title="Minimalism at its finest" sub="Clean architectural lines with everything hidden from view." />
          <FeatureList items={INVISIBLE} testid="invisible-list" />
        </div>
      </section>

      {/* ARCHITECTURAL DETAILS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Details" title="Architectural details" sub="Luxury is found in the details." />
          <Chips items={ARCHITECTURAL} />
        </div>
      </section>

      {/* HOTEL-INSPIRED */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Hotel-Inspired</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Bring five-star elegance home</h2>
            <FeatureList items={HOTEL} dark testid="hotel-list" />
          </div>
          <div className="reveal-right">
            <div className="shape-soft"><img src={IMG.hotelB} alt="Hotel-inspired bathroom" className="w-full h-[480px] object-cover" /></div>
          </div>
        </div>
      </section>

      {/* SMALL BATHROOM */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Small Spaces" title="Make every inch count" sub="Thoughtful design can make compact bathrooms feel open and luxurious." />
          <FeatureList items={SMALL_BATH} testid="small-bath-list" />
        </div>
      </section>

      {/* ACCESSIBILITY + VENTILATION */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <div className="reveal-left">
            <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-2">Accessibility & universal design</h3>
            <p className="text-[#6b7683] mb-6">Comfort for every stage of life.</p>
            <div className="space-y-3">
              {ACCESSIBILITY.map((a) => (
                <div key={a} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3.5 border border-[#eef1f4]">
                  <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="font-medium text-[#0f2740]">{a}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal-right">
            <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-2">Ventilation & waterproofing</h3>
            <p className="text-[#6b7683] mb-6">Protect your investment for the long term.</p>
            <div className="space-y-3">
              {VENTILATION.map((v) => (
                <div key={v} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3.5 border border-[#eef1f4]">
                  <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Droplet className="w-3.5 h-3.5 text-white" /></span>
                  <span className="font-medium text-[#0f2740]">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Transformation" title="Before & after" sub="See the transformation." />
          <div className="max-w-3xl mx-auto reveal"><BeforeAfterSlider before={IMG.scandB} after={IMG.hero} label="Bathroom transformation" /></div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Inspiration" title="Bathroom inspiration gallery" sub="Browse real projects and discover ideas for your own renovation." />
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <button key={i} onClick={() => setLightbox(g)} className="mb-4 block w-full shape-soft group" data-testid={`gallery-item-${i}`}>
                <img src={g} alt={`Bathroom inspiration ${i + 1}`} loading="lazy" className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${i % 3 === 0 ? "h-72" : i % 3 === 1 ? "h-56" : "h-64"}`} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container-x max-w-3xl">
          <Heading center eyebrow="FAQ" title="Bathroom renovation FAQ" />
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="border border-[#e7e9ec] rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left" data-testid={`faq-${i}`}>
                  <span className={`font-display font-semibold ${openFaq === i ? "text-[#2f9fe6]" : "text-[#0f2740]"}`}>{f.q}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${openFaq === i ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740]"}`}>{openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? "max-h-60" : "max-h-0"}`}><p className="px-6 pb-5 text-[#6b7683] leading-relaxed">{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Bathroom" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </>
  );
}
